import { forwardRef, useEffect, useRef, useState } from 'react';
import type { CSSProperties, ReactNode, MouseEvent } from 'react';
import { Icon, type IconName } from './Icons';

type ButtonVariant = 'primary' | 'ghost' | 'subtle' | 'up' | 'down';
type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: IconName;
  children?: ReactNode;
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void;
  style?: CSSProperties;
  disabled?: boolean;
  /** Defaults to "button" so loose buttons in forms don't accidentally submit. */
  type?: 'button' | 'submit' | 'reset';
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', icon, children, onClick, style, disabled, type = 'button' },
  ref,
) {
  const base: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    fontFamily: 'var(--tk-sans)',
    fontWeight: 600,
    border: '1px solid transparent',
    borderRadius: 'var(--tk-radius-sm)',
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'all 0.18s var(--tk-ease)',
    letterSpacing: '-0.005em',
    whiteSpace: 'nowrap',
  };
  const sizes: Record<ButtonSize, CSSProperties> = {
    sm: { padding: '7px 12px', fontSize: 13 },
    md: { padding: '10px 16px', fontSize: 14 },
    lg: { padding: '12px 20px', fontSize: 15 },
  };
  const variants: Record<ButtonVariant, CSSProperties> = {
    primary: { background: 'var(--tk-accent)', color: 'var(--tk-accent-ink)' },
    ghost: { background: 'transparent', color: 'var(--tk-ink)', border: '1px solid var(--tk-line)' },
    subtle: { background: 'var(--tk-surface-2)', color: 'var(--tk-ink)' },
    up: { background: 'var(--tk-up-soft)', color: 'var(--tk-up)' },
    down: { background: 'var(--tk-down-soft)', color: 'var(--tk-down)' },
  };
  return (
    <button
      ref={ref}
      type={type}
      onClick={onClick}
      disabled={disabled}
      style={{ ...base, ...sizes[size], ...variants[variant], ...style }}
      onMouseEnter={(e) => (e.currentTarget.style.transform = 'translateY(-1px)')}
      onMouseLeave={(e) => (e.currentTarget.style.transform = 'translateY(0)')}
    >
      {icon && <Icon name={icon} size={size === 'sm' ? 14 : 16} />}
      {children}
    </button>
  );
});

interface ChipProps {
  active?: boolean;
  children: ReactNode;
  onClick?: () => void;
}

export const Chip = ({ active, children, onClick }: ChipProps) => (
  <button
    onClick={onClick}
    style={{
      padding: '6px 12px',
      fontSize: 12.5,
      fontFamily: 'var(--tk-sans)',
      fontWeight: 600,
      letterSpacing: '0.01em',
      borderRadius: 999,
      border: '1px solid var(--tk-line)',
      background: active ? 'var(--tk-ink)' : 'transparent',
      color: active ? 'var(--tk-bg)' : 'var(--tk-ink-2)',
      cursor: 'pointer',
      transition: 'all 0.18s var(--tk-ease)',
    }}
  >
    {children}
  </button>
);

interface CardProps {
  children: ReactNode;
  padding?: number;
  style?: CSSProperties;
  hover?: boolean;
}

export const Card = ({ children, padding = 24, style, hover }: CardProps) => (
  <div
    style={{
      background: 'var(--tk-card-bg, var(--tk-surface))',
      border: 'var(--tk-card-border, 1px solid var(--tk-line))',
      boxShadow: 'var(--tk-card-shadow, none)',
      borderRadius: 'var(--tk-radius-lg)',
      padding,
      transition: 'all 0.2s var(--tk-ease)',
      ...style,
    }}
    onMouseEnter={
      hover
        ? (e) => {
            e.currentTarget.style.borderColor = 'var(--tk-ink-3)';
            e.currentTarget.style.transform = 'translateY(-1px)';
          }
        : undefined
    }
    onMouseLeave={
      hover
        ? (e) => {
            e.currentTarget.style.borderColor = 'var(--tk-line)';
            e.currentTarget.style.transform = 'translateY(0)';
          }
        : undefined
    }
  >
    {children}
  </div>
);

interface TickerValueProps {
  value: number;
  prefix?: string;
  decimals?: number;
  big?: boolean;
  flash?: boolean;
}

export const TickerValue = ({
  value,
  prefix = '',
  decimals = 2,
  big = false,
  flash = true,
}: TickerValueProps) => {
  const [display, setDisplay] = useState(value);
  const [flashCls, setFlashCls] = useState('');
  const prevRef = useRef(value);

  useEffect(() => {
    const prev = prevRef.current;
    if (prev === value) return;
    if (flash) {
      setFlashCls(value > prev ? 'tk-flash-up' : 'tk-flash-down');
      const t = setTimeout(() => setFlashCls(''), 900);
      return () => clearTimeout(t);
    }
  }, [value, flash]);

  useEffect(() => {
    const start = prevRef.current;
    const end = value;
    const dur = 420;
    const t0 = performance.now();
    let raf = 0;
    const tick = (t: number) => {
      const k = Math.min(1, (t - t0) / dur);
      const e = 1 - Math.pow(1 - k, 3);
      setDisplay(start + (end - start) * e);
      if (k < 1) raf = requestAnimationFrame(tick);
      else prevRef.current = end;
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value]);

  const formatted = display.toLocaleString('en-PK', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

  return (
    <span
      className={`mono ${flashCls}`}
      style={{
        fontWeight: big ? 600 : 500,
        fontSize: big ? 'inherit' : undefined,
        letterSpacing: '-0.02em',
      }}
    >
      {prefix}
      {formatted}
    </span>
  );
};

interface DeltaProps {
  value: number;
  suffix?: string;
  size?: 'sm' | 'md' | 'lg';
  subtle?: boolean;
}

export const Delta = ({ value, suffix = '%', size = 'md', subtle = false }: DeltaProps) => {
  const up = value >= 0;
  const fontSize = size === 'sm' ? 12 : size === 'lg' ? 15 : 13;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 3,
        padding: subtle ? 0 : '3px 8px',
        borderRadius: 6,
        background: subtle
          ? 'transparent'
          : up
            ? 'var(--tk-up-soft)'
            : 'var(--tk-down-soft)',
        color: up ? 'var(--tk-up)' : 'var(--tk-down)',
        fontFamily: 'var(--tk-mono)',
        fontWeight: 600,
        fontSize,
        letterSpacing: '-0.01em',
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      <Icon name={up ? 'arrowUp' : 'arrowDown'} size={fontSize} stroke={2.4} />
      {Math.abs(value).toFixed(2)}
      {suffix}
    </span>
  );
};
